import React, { useState } from 'react';
import { getFreightAdvice } from '../services/geminiService';
import { FreightItem, CalculationResult } from '../types';
import ReactMarkdown from 'react-markdown';
import { useLanguage } from '../contexts/LanguageContext';

interface AIBrokerProps {
  items: FreightItem[];
  result: CalculationResult;
}

export const AIBroker: React.FC<AIBrokerProps> = ({ items, result }) => {
  const [advice, setAdvice] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  
  // Use Context
  const { language, t } = useLanguage();

  const handleAskAI = async () => {
    setLoading(true);
    setHasFetched(true);
    // Pass language to service
    const response = await getFreightAdvice(items, result, language);
    setAdvice(response);
    setLoading(false);
  };

  return (
    <div className="no-print mt-12 relative overflow-hidden rounded-2xl shadow-2xl">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900"></div>
      
      {/* Content Container */}
      <div className="relative z-10">
          <div className="p-8 border-b border-white/10 flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-4">
              <div className="bg-indigo-500/20 p-3 rounded-xl border border-indigo-400/30 backdrop-blur-sm">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-indigo-300">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                </svg>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white tracking-tight">{t('aiTitle')}</h3>
                <p className="text-indigo-200 text-sm mt-1">
                    {t('aiDesc')}
                </p>
              </div>
            </div>
            
            {!loading && (
              <button 
                onClick={handleAskAI}
                className="w-full md:w-auto px-6 py-3 bg-white text-indigo-900 hover:bg-indigo-50 font-bold rounded-xl transition-all shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
              >
                {hasFetched ? t('aiBtnAgain') : t('aiBtnStart')}
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                </svg>
              </button>
            )}
          </div>

          <div className="p-8 min-h-[150px] bg-slate-900/50 backdrop-blur-sm">
            {loading ? (
                <div className="flex flex-col items-center justify-center space-y-4 py-6">
                    <div className="relative w-12 h-12">
                        <div className="absolute top-0 left-0 w-full h-full border-4 border-indigo-500/30 rounded-full"></div>
                        <div className="absolute top-0 left-0 w-full h-full border-4 border-indigo-400 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                    <p className="text-indigo-200 font-medium animate-pulse">{t('aiLoading')}</p>
                </div>
            ) : !advice ? (
                <div className="text-center py-6 opacity-60">
                     <p className="text-indigo-100/60 text-lg">{t('aiPlaceholder')}</p>
                </div>
            ) : (
                <div className="prose prose-invert prose-indigo max-w-none text-slate-100">
                    <ReactMarkdown>{advice}</ReactMarkdown>
                </div>
            )}
          </div>
      </div>
    </div>
  );
};